import { usePlayer } from '../context/PlayerContext';
import YouTubePlayer from './YouTubePlayer';
import ShareCard from './ShareCard';
import HeartButton from './HeartButton';
import { Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, ChevronDown, Repeat1, ListMusic, X, Share2, Video, VideoOff, Disc3 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';

const fmt = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const Player = () => {
  const {
    currentSong, isPlaying, isExpanded, showVideo, togglePlay, next, prev,
    shuffle, toggleShuffle, repeatMode, cycleRepeat,
    currentTime, duration, seekTo, expand, minimize, queue, currentIndex,
    playQueue, toggleShowVideo,
  } = usePlayer();
  const [showQueue, setShowQueue] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const lastTrackedId = useRef(null);

  // Track play count once per song play
  useEffect(() => {
    if (!currentSong) return;
    if (lastTrackedId.current === currentSong.id) return;
    lastTrackedId.current = currentSong.id;
    api.post(`/songs/${currentSong.id}/play`).catch(() => {});
  }, [currentSong]);

  if (!currentSong) return null;

  const RepeatIcon = repeatMode === 'one' ? Repeat1 : Repeat;

  return (
    <>
      {/* Full-screen expanded player UI (no video here — the persistent mount overlays it) */}
      {isExpanded && (
        <div
          data-testid="full-player"
          className="fixed inset-0 z-40 bg-[#120505] slide-up flex flex-col"
        >
          <div className="flex items-center justify-between p-4 border-b border-[#3D1A1A]">
            <button
              data-testid="minimize-player-btn"
              onClick={minimize}
              className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-[#220A0A] transition-colors"
              aria-label="Minimize player"
            >
              <ChevronDown className="w-6 h-6 text-[#FFF3E0]" />
            </button>
            <div className="text-center flex-1 px-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#BFA5A5]">Abhi Baj Raha Hai</div>
              <div className="text-sm font-body text-[#FFF3E0] truncate">Patna Ke Presleys</div>
            </div>
            <button
              data-testid="video-toggle-btn"
              onClick={toggleShowVideo}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${showVideo ? 'bg-[#FF007F]/20 text-[#FF007F]' : 'hover:bg-[#220A0A] text-[#FFF3E0]'}`}
              aria-label={showVideo ? 'Hide video' : 'Show video'}
              title={showVideo ? 'Hide video' : 'Show video'}
            >
              {showVideo ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
            </button>
            <button
              data-testid="share-open-btn"
              onClick={() => setShowShare(true)}
              className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-[#220A0A] transition-colors"
              aria-label="Share this song"
            >
              <Share2 className="w-5 h-5 text-[#FFF3E0]" />
            </button>
            <button
              data-testid="queue-toggle-btn"
              onClick={() => setShowQueue((s) => !s)}
              className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-[#220A0A] transition-colors"
              aria-label="Show queue"
            >
              <ListMusic className="w-5 h-5 text-[#FFF3E0]" />
            </button>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-6 py-4 gap-6 overflow-y-auto">
            {/* Album art (audio-only) OR video slot the persistent iframe overlays */}
            {showVideo ? (
              <div
                data-testid="video-slot"
                className="w-full max-w-md aspect-video rounded-2xl bg-black/40 border border-[#3D1A1A]"
                aria-hidden
              />
            ) : (
              <div
                data-testid="album-art"
                className="relative w-full max-w-xs aspect-square rounded-2xl overflow-hidden border border-[#3D1A1A] shadow-[0_20px_60px_rgba(255,0,127,0.2)]"
              >
                <img
                  src={currentSong.thumbnail_url}
                  alt={currentSong.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#120505]/60" />
                <div className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-[#120505]/80 backdrop-blur border border-[#3D1A1A] flex items-center justify-center">
                  <Disc3 className={`w-5 h-5 text-[#FFB800] ${isPlaying ? 'animate-spin' : ''}`} style={{ animationDuration: '4s' }} />
                </div>
              </div>
            )}

            <div className="w-full max-w-md text-left flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <h2 data-testid="current-song-title" className="font-display text-2xl sm:text-3xl text-[#FFF3E0] leading-tight">
                  {currentSong.title}
                </h2>
                <p className="text-sm text-[#BFA5A5] mt-1">{currentSong.artist}</p>
              </div>
              <HeartButton song={currentSong} size="lg" testId="player-heart-btn" />
            </div>

            <div className="w-full max-w-md">
              <input
                data-testid="seek-slider"
                type="range"
                min={0}
                max={duration || 0}
                value={currentTime || 0}
                onChange={(e) => seekTo(Number(e.target.value))}
                className="w-full accent-[#FFB800] cursor-pointer"
              />
              <div className="flex justify-between text-xs text-[#BFA5A5] mt-1 font-body">
                <span>{fmt(currentTime)}</span>
                <span>{fmt(duration)}</span>
              </div>
            </div>

            <div className="flex items-center gap-6 sm:gap-8">
              <button data-testid="shuffle-btn" onClick={toggleShuffle}
                className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${shuffle ? 'text-[#FF007F]' : 'text-[#BFA5A5]'} hover:text-[#FFF3E0]`}
                aria-label="Shuffle">
                <Shuffle className="w-5 h-5" />
              </button>
              <button data-testid="prev-btn" onClick={prev}
                className="w-12 h-12 rounded-full flex items-center justify-center text-[#FFF3E0] hover:bg-[#220A0A] transition-colors"
                aria-label="Previous">
                <SkipBack className="w-7 h-7" />
              </button>
              <button data-testid="play-pause-btn" onClick={togglePlay}
                className="w-16 h-16 rounded-full bg-[#FFB800] text-[#120505] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform shadow-[0_6px_24px_rgba(255,184,0,0.35)]"
                aria-label={isPlaying ? 'Pause' : 'Play'}>
                {isPlaying ? <Pause className="w-7 h-7 fill-current" /> : <Play className="w-7 h-7 fill-current ml-1" />}
              </button>
              <button data-testid="next-btn" onClick={next}
                className="w-12 h-12 rounded-full flex items-center justify-center text-[#FFF3E0] hover:bg-[#220A0A] transition-colors"
                aria-label="Next">
                <SkipForward className="w-7 h-7" />
              </button>
              <button data-testid="repeat-btn" onClick={cycleRepeat}
                className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${repeatMode !== 'off' ? 'text-[#FF007F]' : 'text-[#BFA5A5]'} hover:text-[#FFF3E0]`}
                aria-label="Repeat">
                <RepeatIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Queue panel */}
          {showQueue && (
            <div data-testid="queue-panel"
              className="absolute inset-0 bg-[#120505]/95 backdrop-blur-md z-10 flex flex-col slide-up">
              <div className="flex items-center justify-between p-4 border-b border-[#3D1A1A]">
                <h3 className="font-display text-xl text-[#FFF3E0]">Agla Geet</h3>
                <button onClick={() => setShowQueue(false)}
                  className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#220A0A]"
                  aria-label="Close queue">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {queue.map((s, i) => (
                  <button data-testid={`queue-item-${i}`} key={s.id + i}
                    onClick={() => { playQueue(queue, i); setShowQueue(false); }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl text-left hover:bg-[#220A0A] transition-colors ${i === currentIndex ? 'bg-[#220A0A]' : ''}`}>
                    <img src={s.thumbnail_url} alt="" className="w-12 h-12 rounded-md object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className={`truncate text-sm ${i === currentIndex ? 'text-[#FFB800]' : 'text-[#FFF3E0]'}`}>{s.title}</div>
                      <div className="text-xs text-[#BFA5A5] truncate">{s.artist}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mini player — pinned to the top so it never gets covered by the on-screen keyboard
          or bottom notification bar; music keeps playing across page navigation and when
          the browser window is minimized. */}
      <div
        data-testid="mini-player"
        onClick={expand}
        role="button"
        tabIndex={0}
        className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#120505]/95 border-b border-[#3D1A1A] cursor-pointer shadow-[0_6px_24px_rgba(0,0,0,0.35)] transition-transform ${isExpanded ? '-translate-y-full pointer-events-none' : 'translate-y-0'}`}
      >
        {/* Progress bar at very top edge — reads like a thin FM tuning strip */}
        <div className="h-0.5 bg-[#3D1A1A]">
          <div className="h-full bg-[#FF007F] transition-all"
            style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }} />
        </div>
        <div className="flex items-center gap-3 p-2.5 max-w-5xl mx-auto">
          <img data-testid="mini-thumb" src={currentSong.thumbnail_url || undefined} alt="" className="w-11 h-11 rounded-md object-cover bg-[#3D1A1A]" />
          <div className="flex-1 min-w-0">
            <div data-testid="mini-title" className="truncate text-sm text-[#FFF3E0]">{currentSong.title}</div>
            <div data-testid="mini-artist" className="text-xs text-[#BFA5A5] truncate">{currentSong.artist}</div>
          </div>
          <button data-testid="mini-prev-btn" onClick={(e) => { e.stopPropagation(); prev(); }}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#220A0A]" aria-label="Previous">
            <SkipBack className="w-5 h-5 text-[#FFF3E0]" />
          </button>
          <button data-testid="mini-play-pause-btn" onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            className="w-11 h-11 rounded-full bg-[#FFB800] text-[#120505] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
            aria-label={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current ml-0.5" />}
          </button>
          <button data-testid="mini-next-btn" onClick={(e) => { e.stopPropagation(); next(); }}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#220A0A]" aria-label="Next">
            <SkipForward className="w-5 h-5 text-[#FFF3E0]" />
          </button>
        </div>
      </div>

      {/* PERSISTENT VIDEO MOUNT — always alive, repositioned via CSS.
          This is the ONE place the YouTube iframe lives so playback never restarts.
          When audio-only (default) OR minimized, it lives off-screen so audio keeps
          streaming without showing any video. When the user opts in via the
          video toggle, it snaps over the video slot in the expanded player. */}
      <div
        data-testid="persistent-video"
        className={`fixed z-[45] rounded-2xl overflow-hidden bg-black transition-opacity duration-300 ease-out ${
          isExpanded && showVideo
            ? 'top-[92px] left-1/2 -translate-x-1/2 w-[calc(100vw-48px)] max-w-md aspect-video border border-[#3D1A1A] shadow-[0_10px_60px_rgba(255,0,127,0.15)] opacity-100 pointer-events-auto'
            : 'w-[480px] h-[270px] pointer-events-none opacity-0'
        }`}
        style={
          !(isExpanded && showVideo)
            ? { top: '-9999px', left: '-9999px' }
            : undefined
        }
      >
        <YouTubePlayer visible={true} />
      </div>

      {/* Share card modal */}
      {showShare && (
        <ShareCard song={currentSong} onClose={() => setShowShare(false)} />
      )}
    </>
  );
};

export default Player;
